import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { getErrorMessage } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { 
  Settings as SettingsIcon, Sun, Moon, Palette, Lock,
  Mail, Trash2, Loader2, AlertTriangle, Key
} from "lucide-react";

export default function Settings() {
  const { user, isAdmin, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  // States for settings
  const [sendingReset, setSendingReset] = useState(false);
  const [account, setAccount] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [requestingEmailChange, setRequestingEmailChange] = useState(false);
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  useEffect(() => {
    if (!user?.account_id) return;
    axios.get(`${API}/accounts/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAccount(res.data))
      .catch(() => {});
  }, [user, token]);

  const handleResetPassword = async () => {
    if (!user?.email) {
      toast.error("No email found for your account");
      return;
    }
    
    setSendingReset(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: user.email
      });
      
      if (response.data.email_sent) {
        toast.success("Password reset link sent to your email");
      } else {
        toast.info("If this email is registered, a reset link will be sent");
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error.response?.data?.detail, "Failed to send reset link");
      toast.error(errorMessage);
    } finally {
      setSendingReset(false);
    }
  };

  const handleRequestEmailChange = async () => {
    const email = newEmail.trim();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Enter a valid new email address");
      return;
    }
    const current = account?.primary_contact?.email || user?.email;
    if (current && email.toLowerCase() === current.toLowerCase()) {
      toast.error("That's already your primary email");
      return;
    }
    setRequestingEmailChange(true);
    try {
      const pc = account?.primary_contact || {};
      await axios.put(
        `${API}/accounts/me`,
        { primary_contact: { name: pc.name || null, phone: pc.phone || null, email } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Verification sent to ${email}. Click the link there to confirm the change.`);
      setNewEmail("");
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, "Failed to request email change"));
    } finally {
      setRequestingEmailChange(false);
    }
  };

  const handleRequestDeletion = () => {
    setRequestingDeletion(true);
    setTimeout(() => {
      toast.success("Account deletion request submitted. Our team will contact you within 48 hours.");
      setRequestingDeletion(false);
    }, 1500);
  };


  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account security and preferences
        </p>
      </div>

      {/* Password Reset */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Key className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>Send a password reset link to your registered email</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                Current email: <span className="font-medium text-foreground">{user?.email}</span>
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleResetPassword}
              disabled={sendingReset}
            >
              {sendingReset ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Reset Link
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Request Email Change */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Change Primary Email</CardTitle>
              <CardDescription>Request to change your account's primary email address</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Enter your new email address. We'll send a verification link to it. The change takes effect once you confirm.
            {(account?.primary_contact?.email || user?.email) && (
              <span className="block mt-1">Current: <span className="font-medium text-foreground">{account?.primary_contact?.email || user?.email}</span></span>
            )}
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Input
              type="email"
              placeholder="new@email.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={handleRequestEmailChange}
              disabled={requestingEmailChange || !newEmail.trim()}
              className="shrink-0"
            >
              {requestingEmailChange ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Request Change"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account delegation lives in the Team tab now - single place to invite
          and manage members with roles. (#11) */}

      {/* Appearance */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Palette className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Choose your preferred theme</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => theme !== "light" && toggleTheme()}
              className={`p-4 rounded-xl border-2 transition-all ${
                theme === "light" 
                  ? "border-primary ring-2 ring-primary/20" 
                  : "border-border hover:border-muted-foreground/50"
              }`}
              data-testid="theme-light-btn"
            >
              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <div className="h-2 w-16 bg-gray-800 rounded"></div>
                </div>
                <div className="space-y-1">
                  <div className="h-2 w-full bg-gray-200 rounded"></div>
                  <div className="h-2 w-3/4 bg-gray-200 rounded"></div>
                </div>
              </div>
              <p className="text-sm font-medium mt-3 flex items-center justify-center gap-2">
                <Sun className="h-4 w-4" /> Light
              </p>
            </button>
            
            <button
              onClick={() => theme !== "dark" && toggleTheme()}
              className={`p-4 rounded-xl border-2 transition-all ${
                theme === "dark" 
                  ? "border-primary ring-2 ring-primary/20" 
                  : "border-border hover:border-muted-foreground/50"
              }`}
              data-testid="theme-dark-btn"
            >
              <div className="bg-zinc-900 rounded-lg p-3 border border-zinc-700">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <div className="h-2 w-16 bg-zinc-100 rounded"></div>
                </div>
                <div className="space-y-1">
                  <div className="h-2 w-full bg-zinc-700 rounded"></div>
                  <div className="h-2 w-3/4 bg-zinc-700 rounded"></div>
                </div>
              </div>
              <p className="text-sm font-medium mt-3 flex items-center justify-center gap-2">
                <Moon className="h-4 w-4" /> Dark
              </p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Request Account Deletion (Advertisers only) */}
      {!isAdmin && (
        <Card className="bg-card border-border border-destructive/30">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-destructive/10">
                <Trash2 className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-destructive">Request Account Deletion</CardTitle>
                <CardDescription>Permanently delete your account and all associated data</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">This action cannot be undone</p>
                  <p className="text-muted-foreground mt-1">
                    Deleting your account will permanently remove all your campaigns, creatives, locations, and billing history. 
                    Any outstanding balances must be settled before deletion.
                  </p>
                </div>
              </div>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Request Account Deletion
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirm Account Deletion
            </DialogTitle>
            <DialogDescription className="pt-2">
              This action is permanent and cannot be undone. All your data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive font-medium">You will lose:</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• All campaigns and their analytics</li>
                <li>• All creatives and company assets</li>
                <li>• All location data</li>
                <li>• Complete billing history</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Type <span className="font-mono font-bold">DELETE</span> to confirm:</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setDeleteConfirmText("");
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmText("");
                handleRequestDeletion();
              }}
              disabled={deleteConfirmText !== "DELETE" || requestingDeletion}
            >
              {requestingDeletion ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete My Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
